import {
  Button,
  Fab,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  makeStyles,
  useTheme
} from '@material-ui/core/styles';
import { AddRounded, EditRounded, PersonAddRounded } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RRLink, useParams } from 'react-router-dom';
import { RootState } from '../../app/store';
import { ORDER } from '../../common/enum/order';
import DocRevisionCard from '../../components/DocRevisionCard';
import DocumentInfoCard from '../../components/DocumentInfoCard';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import { getCookie, updateCookie } from '../../functions/manageCookies';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import { getProjectDocumentRevisionsAsync } from '../ProjectDocumentRevision/projectDocumentRevisionSlice';
import { getProjectDocumentAsync } from './projectDocumentSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    titleBox: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        display: 'block',
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    fileNotFound: {
      padding: theme.spacing(10, 2),
      textAlign: 'center'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    extendedIcon: {
      marginRight: theme.spacing(1)
    },
    actionIcon: {
      position: 'fixed',
      bottom: theme.spacing(3),
      right: theme.spacing(3),
      zIndex: 100,
      paddingRight: theme.spacing(3)
    },
    header: {
      padding: theme.spacing(1.5, 1)
    },
    marginIconButton: {
      margin: theme.spacing(-1, 0, -1, 1)
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    },
    flexGrow: {
      flexGrow: 1
    },
    marginBottom: {
      marginBottom: theme.spacing(1)
    }
  })
);

export interface DocumentRevisionParam {
  projectDocumentId?: number;
  page: number;
  limit: number;
  sortBy?: string | null;
  orderBy?: ORDER | null;
}

export default function DocumentView() {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { user } = useSelector((state: RootState) => state.userProfile);
  const { data: documentRevisions, count: countDocRevs } = useSelector(
    (state: RootState) => state.projectDocumentRevisions
  );
  const { selectedProjectDocument } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const params = useParams<{ docId: string }>();
  const projectDocumentId = Number.parseInt(params.docId, 10);

  const pageKey = `[DOC]-userId${user?.id}-documentId${projectDocumentId}-allRevisions`;
  const cookieParams: DocumentRevisionParam | undefined = getCookie(pageKey);

  const defaultParams: DocumentRevisionParam = {
    projectDocumentId,
    page: 0,
    limit: 30,
    sortBy: null,
    orderBy: null
  };

  const [documentRevisionParams, setDocumentRevisionParams] =
    useState<DocumentRevisionParam>({ ...defaultParams, ...cookieParams });

  useEffect(() => {
    dispatch(getProjectDocumentAsync(projectDocumentId));
    loadingDocumentRevision(documentRevisionParams);
  }, []);

  function loadingDocumentRevision(
    documentRevisionParams: DocumentRevisionParam
  ) {
    updateCookie(pageKey, documentRevisionParams);
    dispatch(getProjectDocumentRevisionsAsync(documentRevisionParams));
    setDocumentRevisionParams(documentRevisionParams);
  }

  function handleChangePage<T>(
    event: React.ChangeEvent<unknown>,
    newPage: number,
    params: T,
    loadingElement: (params: T) => void
  ) {
    loadingElement({
      ...params,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="documentName"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          documentName: selectedProjectDocument?.name,
          documentId: selectedProjectDocument?.id
        }}
      />
      <div className={classes.titleBox}>
        <Typography component="p" variant="h4">
          {t('projectDocument.documentDetails')}
          <Tooltip title={t('projectDocument.editDocument') as string} arrow>
            <IconButton
              className={classes.marginIconButton}
              component={RRLink}
              to={`/documents/${projectDocumentId}/edit`}
              size="small"
            >
              <EditRounded />
            </IconButton>
          </Tooltip>
        </Typography>
        <div className={classes.flexGrow} />
        <Button
          component={RRLink}
          to={`/documents/${projectDocumentId}/share`}
          variant="contained"
          color="primary"
          size="small"
          startIcon={<PersonAddRounded />}
        >
          {t('projectDocument.share')}
        </Button>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
          {selectedProjectDocument && (
            <DocumentInfoCard document={selectedProjectDocument} />
          )}
        </Grid>
        <Grid item xs={12} md={9}>
          <Typography
            className={classes.header}
            variant="body1"
            color="textSecondary"
          >
            {t('projectDocument.revisions')}
          </Typography>
          <Typography
            className={classes.marginBottom}
            variant="body2"
            color="textSecondary"
          >
            {t('common.aboutResults', { count: countDocRevs })}
          </Typography>
          {documentRevisions?.length > 0 && selectedProjectDocument ? (
            <DocRevisionCard<DocumentRevisionParam>
              renderDocRevisions={documentRevisions}
              params={documentRevisionParams}
              setParams={setDocumentRevisionParams}
              document={selectedProjectDocument}
            />
          ) : (
            <div className={classes.fileNotFound}>{t('projectDocument.revisionNotFound')}</div>
          )}
          <div className={classes.pagination}>
            <Pagination
              count={Math.ceil(countDocRevs / documentRevisionParams.limit)}
              shape="rounded"
              page={documentRevisionParams.page + 1}
              onChange={(event, newPage) => {
                handleChangePage<DocumentRevisionParam>(
                  event,
                  newPage,
                  documentRevisionParams,
                  loadingDocumentRevision
                );
              }}
            />
          </div>
        </Grid>
      </Grid>
      <Zoom
        in
        timeout={transitionDuration}
        style={{
          transitionDelay: `${transitionDuration.exit}ms`
        }}
        unmountOnExit
      >
        <Fab
          className={classes.actionIcon}
          component={RRLink}
          to={`/document-revisions/create?documentId=${projectDocumentId}`}
          variant="extended"
          color="primary"
          aria-label={t('projectDocument.addNewRevisionAriaLabel') as string}
        >
          <AddRounded className={classes.extendedIcon} />
          {t('projectDocument.newDocumentRevision')}
        </Fab>
      </Zoom>
    </div>
  );
}
